import React, { useState, useEffect } from 'react';
import './Sidebar.css';

/**
 * Sidebar Component
 * 
 * Un componente sidebar moderno y profesional que se desliza desde la izquierda.
 * Características:
 * - Position fixed para estar por encima del contenido
 * - Animaciones suaves de entrada/salida
 * - Scroll independiente que no afecta al resto de la página
 * - Backdrop opcional con opacidad
 * - Completamente personalizable
 * 
 * @param {boolean} isOpen - Controla si el sidebar está abierto o cerrado
 * @param {function} onClose - Función callback cuando se cierra el sidebar
 * @param {React.ReactNode} children - Contenido del sidebar
 * @param {string} width - Ancho del sidebar (default: '280px')
 * @param {boolean} showBackdrop - Mostrar fondo oscuro detrás (default: true)
 * @param {string} position - Posición del sidebar: 'left' o 'right' (default: 'left')
 */
const Sidebar = ({
    isOpen = false,
    onClose,
    children,
    width = '280px',
    showBackdrop = true,
    position = 'left'
}) => {
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setIsAnimating(true);
            // Prevenir scroll del body cuando el sidebar está abierto
            document.body.style.overflow = 'hidden';
        } else {
            // Restaurar scroll del body cuando se cierra
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Manejar click en el backdrop
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && onClose) {
            onClose();
        }
    };

    // Manejar ESC key para cerrar
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen && onClose) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    return (
        <>
            {/* Backdrop - Fondo oscuro semitransparente */}
            {showBackdrop && (
                <div
                    className={`sidebar-backdrop ${isOpen ? 'sidebar-backdrop--visible' : ''}`}
                    onClick={handleBackdropClick}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar Container */}
            <aside
                className={`sidebar sidebar--${position} ${isOpen ? 'sidebar--open' : ''}`}
                style={{
                    width: width,
                    [position]: isOpen ? '0' : `-${width}`
                }}
                aria-hidden={!isOpen}
                role="complementary"
            >
                {/* Sidebar Content con scroll independiente */}
                <div className="sidebar-content">
                    {children}
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
